import type { ITableLikeTreeFilterItem } from 'components/TableLikeTree/context';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderUpdateSeasonsGql } from '../../bimWorkOrderQueries';

const updateWorkOrderSeasonsRequestId = 'bimWorkOrderSeasonsUpdate';

type IBimSeasonValidationMessages = {
    metadata: { contractObjectId?: number; workOrderObjectId?: number };
    message: string;
};

export const useBimUpdateWorkOrderSeasons = ({
    workOrderId,
    updateSeasonRange,
}: {
    workOrderId: number | undefined;
    updateSeasonRange: () => void;
}) => {
    const dispatch = useDispatch();

    const [validationMessages, setValidationMessages] = useState<IBimSeasonValidationMessages[]>([]);

    const [bimUpdateSeasonDataReset] = useResetStoreData({ requestId: updateWorkOrderSeasonsRequestId });

    const reset = useCallback(() => {
        bimUpdateSeasonDataReset(undefined);
    }, [bimUpdateSeasonDataReset]);

    const [updateWorkOrderSeasonsData, updateWorkOrderSeasonsStatus, , updateWorkOrderSeasonsMakeRequest] = useRequest({
        requestId: updateWorkOrderSeasonsRequestId,
        query: bimWorkOrderUpdateSeasonsGql,
        withEffect: false,
        dependenciesUpdater: updateSeasonRange,
    });

    const updateSeasonsHandler = useCallback(
        (seasonsData: {
            firstSeason: ITableLikeTreeFilterItem | undefined;
            lastSeason: ITableLikeTreeFilterItem | undefined;
        }) => {
            if (workOrderId) {
                setValidationMessages([]);
                dispatch(
                    updateWorkOrderSeasonsMakeRequest({
                        workOrderId,
                        input: {
                            firstContractSeasonId: seasonsData?.firstSeason?.id,
                            lastContractSeasonId: seasonsData?.lastSeason?.id,
                        },
                    }),
                );
            }
        },
        [dispatch, updateWorkOrderSeasonsMakeRequest, workOrderId],
    );

    useEffect(() => {
        if (updateWorkOrderSeasonsData?.bimUpdateWorkOrderSeasons?.validation?.length) {
            setValidationMessages(
                updateWorkOrderSeasonsData?.bimUpdateWorkOrderSeasons?.validation?.map((v) => {
                    let metadata: { contractObjectId?: number; workOrderObjectId?: number };

                    try {
                        metadata = JSON.parse(v.metadata || '');
                    } catch {
                        metadata = {};
                    }

                    return { message: v.message, metadata };
                }) || [],
            );
        }
    }, [updateSeasonRange, updateWorkOrderSeasonsData?.bimUpdateWorkOrderSeasons?.validation]);

    useEffect(() => {
        reset();
    }, [reset]);

    return { updateSeasonsHandler, updateSeasonsValidationMessages: validationMessages, updateWorkOrderSeasonsStatus };
};
